// ---------------------------------------------------------------------------
// Campaign lead search (G2 / G3) — minimal, client-side
//
// G2 (find by name) and G3 (find by company or email) were reported "not
// implemented". This is the deliberately small version decided on 27 Aug: a
// case-insensitive substring match over the name, company and email of the
// leads ALREADY LOADED on screen. No server endpoint, no cross-page search, no
// fuzzy matching, no ranking, no combining with other filters — those are
// explicitly later.
//
// The one real catch is honesty about scope. The search can only see what is
// loaded; a hit on an unloaded page would read as "not in the campaign", which
// is worse than no search at all. `leadSearchSummary` carries that caveat. It
// is dormant today (GET /api/campaigns/:id/leads returns every lead in one
// request and the table renders them all, so the whole campaign IS loaded), and
// it turns itself on the moment server-side paging is introduced — the same
// "never a number we can't stand behind" rule as the LinkedIn limit and the
// bounce counter.
// ---------------------------------------------------------------------------

/** The lead fields the campaign search reads. All optional so partial rows are safe. */
export interface SearchableLead {
  firstName?: string;
  lastName?: string;
  company?: string;
  email?: string;
}

/**
 * Does a lead match the search query? Case-insensitive substring over the full
 * name ("first last"), company and email. A blank query matches every lead — an
 * empty search box is not a filter.
 */
export function leadMatchesSearch(lead: SearchableLead, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const fields = [
    `${lead.firstName ?? ''} ${lead.lastName ?? ''}`.trim(),
    lead.company ?? '',
    lead.email ?? '',
  ];
  return fields.some(f => f.toLowerCase().includes(q));
}

/** Narrow a lead list by the search query. A blank query returns the list unchanged. */
export function filterLeadsBySearch<T extends SearchableLead>(leads: readonly T[], query: string): T[] {
  if (!query.trim()) return [...leads];
  return leads.filter(l => leadMatchesSearch(l, query));
}

/**
 * The truthful count shown for an active search, e.g. "3 of 250". When the
 * loaded leads are NOT the whole campaign, the label says the search only
 * covered what is loaded, so a hidden lead on an unloaded page is never read as
 * absent. `allLeadsLoaded` is true today (see the module header) and the
 * qualifier stays off until server-side paging makes it false.
 */
export function leadSearchSummary(
  matchCount: number,
  loadedCount: number,
  opts: { allLeadsLoaded: boolean },
): string {
  const base = `${matchCount} of ${loadedCount}`;
  return opts.allLeadsLoaded ? base : `${base} loaded — search covers loaded leads only`;
}
